/**
 * <editorial-card-form>
 *
 * Progressive enhancement for the editorial collection card. Lets the shopper pick
 * a variant from inline pills and keeps the price, discount, add-to-cart button and
 * card link in sync. Without JS the card still adds the first/default variant.
 */
class EditorialCardForm extends HTMLElement {
  connectedCallback() {
    this.options = this.querySelector('.editorial-card__options');
    if (!this.options) return;

    this.variantInput = this.querySelector('[data-ec-variant-id]');
    this.priceCurrent = this.querySelector('[data-ec-price-current]');
    this.priceWas = this.querySelector('[data-ec-price-was]');
    this.priceOff = this.querySelector('[data-ec-price-off]');
    this.button = this.querySelector('.editorial-card__atc');
    this.card = this.closest('product-card');

    this.options.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.options?.removeEventListener('change', this.#onChange);
  }

  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    for (const label of this.options.querySelectorAll('.editorial-card__option')) {
      label.classList.toggle('is-selected', label.contains(input));
    }

    const { variantId, available, price, compare, url } = input.dataset;
    const isAvailable = available === 'true';

    if (this.variantInput && variantId) {
      this.variantInput.value = variantId;
      this.variantInput.disabled = !isAvailable;
    }

    if (this.priceCurrent && price) this.priceCurrent.textContent = price;

    if (this.priceWas) {
      if (compare) {
        this.priceWas.textContent = compare;
        this.priceWas.hidden = false;
      } else {
        this.priceWas.hidden = true;
      }
    }

    if (this.priceOff) {
      const pct = this.#discountPercent(price, compare);
      if (pct) {
        this.priceOff.textContent = `${pct}% off`;
        this.priceOff.hidden = false;
      } else {
        this.priceOff.hidden = true;
      }
    }

    if (this.button) {
      this.button.disabled = !isAvailable;
      const label = this.button.querySelector('.add-to-cart-text__content span span');
      if (label) label.textContent = isAvailable ? 'Add to cart' : 'Sold out';
    }

    const link = this.card?.querySelector('[ref="productCardLink"]');
    if (link instanceof HTMLAnchorElement && url) link.setAttribute('href', url);
  };

  #discountPercent(priceStr, compareStr) {
    const price = this.#toNumber(priceStr);
    const compare = this.#toNumber(compareStr);
    if (!price || !compare || compare <= price) return 0;
    return Math.round(((compare - price) / compare) * 100);
  }

  #toNumber(value) {
    if (!value) return 0;
    const digits = value.replace(/[^0-9.,]/g, '').replace(/,/g, '');
    return parseFloat(digits) || 0;
  }
}

if (!customElements.get('editorial-card-form')) {
  customElements.define('editorial-card-form', EditorialCardForm);
}
